import { normalizeTitle } from '../../../utils/format';

export type PitchVolumeMetric = 'clicks' | 'impressions';
export type PitchPriceModel = 'flat' | 'cpc' | 'cpm';

export type PitchFormValues = {
  spotName: string;
  spotDescription: string;
  volumeMetric: PitchVolumeMetric | null;
  expectedDailyVolume: number | null;
  priceModel: PitchPriceModel;
  priceUsd: number | null;
  duration: string;
};

export type PitchPrefill = {
  source: 'monday_inventory';
  sourceBoardId?: string;
  sourceItemId?: string;
  spotName?: string;
  spotDescription?: string;
  volumeMetric?: PitchVolumeMetric | null;
  expectedDailyVolume?: number | null;
};

export type PitchCountry = {
  country?: {
    alpha2?: string | null;
    name?: string | null;
  };
  share?: number | null;
};

export type PitchTrafficSource = {
  label: string;
  value: number;
};

export type PitchSimilarwebData = {
  domain: string;
  desktopShare?: number | null;
  mobileShare?: number | null;
  topCountries: PitchCountry[];
  trafficSources: PitchTrafficSource[];
};

export const EMPTY_PITCH_FORM: PitchFormValues = {
  spotName: '',
  spotDescription: '',
  volumeMetric: 'clicks',
  expectedDailyVolume: null,
  priceModel: 'flat',
  priceUsd: null,
  duration: '1 month',
};

type InventoryColumn = { id?: string; title?: string; text?: string };
type InventoryMatchLike = {
  name?: string | null;
  domain?: string | null;
  columns_preview?: InventoryColumn[];
};

const readInventoryColumn = (columns: InventoryColumn[], title: string): string => {
  const wanted = normalizeTitle(title);
  const found = columns.find((col) => normalizeTitle(col?.title) === wanted);
  return (found?.text || '').trim();
};

export const readInventoryGeoText = (match: InventoryMatchLike | null): string | null => {
  const columns = match?.columns_preview || [];
  const column = columns.find((entry) => entry.id === 'text_mkpmds33')
    || columns.find((entry) => normalizeTitle(entry.title) === 'geo');
  const text = column?.text;
  return text?.trim() ? text : null;
};

const SEPARATORS_TO_DROP = /[\s\u00A0\u202F'\u2019]/g;
const NUMERIC_TOKEN = /\d[\d.,]*/;
const GROUPED_THOUSANDS = /^\d{1,3}(?:\.\d{3})+$|^\d{1,3}(?:,\d{3})+$/;
// Dashboard paste like 6.840 or 6.84 (Monday may drop the trailing zero).
const DASHBOARD_THOUSANDS = /^\d{1,4}[.,]\d{2,3}$/;

/** Monday stores volumes in mixed locales, so `.` and `,` may group thousands or mark decimals. */
const integerDigits = (token: string): string => {
  if (!/[.,]/.test(token)) return token;
  if (GROUPED_THOUSANDS.test(token)) return token.replace(/[.,]/g, '');
  if (DASHBOARD_THOUSANDS.test(token)) {
    const separator = Math.max(token.lastIndexOf('.'), token.lastIndexOf(','));
    const whole = token.slice(0, separator).replace(/[.,]/g, '');
    const fraction = token.slice(separator + 1);
    return `${whole}${fraction.padEnd(3, '0')}`;
  }
  const decimalPoint = Math.max(token.lastIndexOf('.'), token.lastIndexOf(','));
  return token.slice(0, decimalPoint).replace(/[.,]/g, '');
};

const parseFirstInteger = (value: string): number | null => {
  const match = value.replace(SEPARATORS_TO_DROP, '').match(NUMERIC_TOKEN);
  if (!match) return null;
  const parsed = Number(integerDigits(match[0].replace(/[.,]+$/, '')));
  return Number.isFinite(parsed) ? parsed : null;
};

const deriveSpotName = (match: InventoryMatchLike): string => {
  const name = (match.name || '').trim();
  if (!name) return '';
  const parts = name.split(' - ');
  return parts.length > 1 ? parts.slice(1).join(' - ').trim() : name;
};

export const prefillFromInventory = (match: InventoryMatchLike): PitchFormValues => {
  const columns = match.columns_preview || [];
  const volumeText = readInventoryColumn(columns, 'Expected daily impressions/clicks');
  const adFormat = readInventoryColumn(columns, 'Ad Format');
  const spotName = deriveSpotName(match) || adFormat;
  return {
    ...EMPTY_PITCH_FORM,
    spotName,
    expectedDailyVolume: parseFirstInteger(volumeText),
  };
};

export type PublisherTerms = {
  status: string;
  adFormat: string;
  dealType: string;
  rate: string;
};

/** Read-only facts from Monday shown alongside the pitch form; never copied into the pitch text. */
export const readPublisherTerms = (match: InventoryMatchLike): PublisherTerms => {
  const columns = match.columns_preview || [];
  return {
    status: readInventoryColumn(columns, 'Inventory item status'),
    adFormat: readInventoryColumn(columns, 'Ad Format'),
    dealType: readInventoryColumn(columns, "Publisher's deal type"),
    rate: readInventoryColumn(columns, 'Publishers rate'),
  };
};

const placeholder = (value: string, label: string): string => value.trim() || `[${label}]`;

const normalizeInline = (value: string): string => value.trim().replace(/\s+/g, ' ');

const inlinePlaceholder = (value: string, label: string): string => (
  normalizeInline(value) || `[${label}]`
);

const formatInteger = (value: number | null, label: string): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return `[${label}]`;
  return Math.round(value).toLocaleString('en-US');
};

const formatPitchPrice = (form: PitchFormValues): string => {
  if (form.priceModel === 'flat') {
    return `$${formatInteger(form.priceUsd, 'price')} - per ${placeholder(form.duration, 'duration')}`;
  }
  const rate = typeof form.priceUsd === 'number' && Number.isFinite(form.priceUsd)
    ? form.priceUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 20 })
    : '[rate]';
  return `$${rate} ${form.priceModel.toUpperCase()}`;
};

const formatRatioPercent = (value?: number | null): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '[unavailable]';
  const percent = Math.abs(value) <= 1 ? value * 100 : value;
  return `${Math.round(percent)}%`;
};

const formatGeoPercent = (value?: number | null): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '[unavailable]';
  const percent = Math.abs(value) <= 1 ? value * 100 : value;
  return `${percent.toFixed(1)}%`;
};

const formatSourcePercent = (value: number): string => `${Math.round(value)}%`;

const canonicalDomain = (domain: string): string => {
  const normalized = domain
    .trim()
    .replace(/^https?:\/\//i, '')
    .split('/')[0]
    .replace(/\.+$/, '');
  return normalized || '[Site name]';
};

const formatTrafficSources = (sources: PitchTrafficSource[]): string => {
  const rows = sources
    .filter((source) => source.label.trim() && Number.isFinite(source.value))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5)
    .map((source) => `${source.label.trim()}: ${formatSourcePercent(source.value)}`);
  return rows.length > 0 ? rows.join(' / ') : '[unavailable]';
};

export const formatTopGeos = (countries: PitchCountry[]): string => {
  const rows = countries
    .slice(0, 10)
    .map((entry) => {
      const country = entry.country?.alpha2?.trim().toUpperCase() || entry.country?.name?.trim();
      return country ? `${country} ${formatGeoPercent(entry.share)}` : null;
    })
    .filter((entry): entry is string => Boolean(entry));
  return rows.length > 0 ? rows.join(', ') : '[unavailable]';
};

export const buildTopGeosLine = (countries: PitchCountry[]): string => (
  `➡️ Expected Geo Breakdown: ${formatTopGeos(countries)}`
);

export const canCopyPitch = (form: PitchFormValues): boolean => form.volumeMetric !== null;

export const buildPitchText = (form: PitchFormValues, data: PitchSimilarwebData): string => {
  const intro = [`${canonicalDomain(data.domain)} ${inlinePlaceholder(form.spotName, 'Spot name')}`];
  const description = normalizeInline(form.spotDescription);
  if (description) intro.push('', description);

  return [
    ...intro,
    '',
    'Traffic Sources',
    `Desktop: ${formatRatioPercent(data.desktopShare)} / Mobile: ${formatRatioPercent(data.mobileShare)}`,
    formatTrafficSources(data.trafficSources),
    '',
    buildTopGeosLine(data.topCountries),
    '',
    `➡️ Expected volumes are about: ${formatInteger(form.expectedDailyVolume, 'daily volume')} daily ${form.volumeMetric || '[metric]'}`,
    '',
    `➡️ Price: ${formatPitchPrice(form)}`,
  ].join('\n');
};

export const hasUnavailableSimilarwebData = (data: PitchSimilarwebData): boolean => (
  typeof data.desktopShare !== 'number'
  || typeof data.mobileShare !== 'number'
  || data.topCountries.length === 0
  || data.trafficSources.length === 0
);
